import { kalbVorAlttier } from "@dj/domain/drueckjagd";
import { ROLLENSYMBOL, type Personenangabe, type Rolle } from "@dj/domain/gemeinsam";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { ortAusRettungspunkten, useAutomatik } from "../design/darstellung";
import { useGestaltung } from "../design/gestaltung";
import { Status } from "../design/Hinweis";
import { Bestaetigung, RueckgaengigLeiste, useRueckgaengig } from "../design/Rueckgaengig";
import { Kachel } from "../design/Kachel";
import { useEingabesperre, useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { useDokument, useSammlung } from "../plattform/lesemodelle";
import { IphoneHinweis } from "../plattform/IphoneHinweis";
import { OfflineBereit, OfflineHinweis } from "../plattform/Offline";
import { letzteMessung } from "../plattform/position";
import { useSitzung } from "../plattform/sitzung";
import { phaseZu, useStartbeitraege, type Kernaktion } from "../plattform/startansicht";
import { beobachten, eintraege, wartend } from "../plattform/warteschlange";
import { DialogRahmen, useAusloeser } from "./Ausloeser";
import { useJagdzustand } from "./jagdzustand";
import { kernaktionenDerReihe, zieleDerStartansicht } from "./ziele";
import { Kontextzeile, Zustandsband } from "./Zustandsband";

/**
 * Startansicht einer Drückjagd (oberflaeche.md §4, design/README §4.4, §10.2; Referenz `#s-start`, `#s-leitung`,
 * `#s-ansteller`, `#s-nachsuche`): Zustandsband → `navigation` „Drückjagd“ (Kernaktionen weiterer Rollen, höchstens drei
 * weitere Ziele nach Priorität, zuletzt „mehr“) → `main` „Startansicht“ mit der Hauptaktion als größter Fläche in
 * Daumenreichweite und dem Notruf zuletzt. Feste Zeilen, nie Scrollen. Die Hauptaktion je Rolle und Phase (§4.3) liefern
 * die Kontexte (plattform/startansicht.ts).
 */
function useWartend(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const lesen = () => void wartend().then(setN);
    lesen();
    return beobachten(lesen);
  }, []);
  return n;
}

/**
 * Hinweis „Kalb vor Alttier“ (F-146, oberflaeche.md §9): nach dem Rückgängig-Fenster einer eigenen Schussmeldung dieser
 * Drückjagd mit einem Alttier, bis das Gerät die nächste Meldung der Drückjagd erfasst. Quelle ist die Warteschlange des
 * Geräts: Der Hinweis braucht kein Netz und erscheint, sobald die Meldung abgeschlossen ist.
 */
function useKalbVorAlttier(drueckjagdId: string): boolean {
  const [an, setAn] = useState(false);
  useEffect(() => {
    let aktiv = true;
    const lesen = () => void eintraege().then((alle) => {
      const letzte = alle.filter((e) => e.daten["Drückjagd"] === drueckjagdId).at(-1);
      if (aktiv) {
        setAn(!!letzte && letzte.befehl === "SchussMelden" && letzte.stand !== "abgewiesen"
          && kalbVorAlttier({ wildart: letzte.daten["Wildart"], altersklasse: letzte.daten["Altersklasse"] }));
      }
    });
    lesen();
    const abmelden = beobachten(lesen);
    return () => { aktiv = false; abmelden(); };
  }, [drueckjagdId]);
  return an;
}

/**
 * Lage der Person für die Ziele der Startansicht (plattform/startansicht.ts): Rollen und Revier aus Meine Drückjagden,
 * Phase der Jagd aus dem Jagdzustand (§4.3). `zieleBekannt`: erst wenn Rollen und Jagdzustand gelesen sind, stehen Ziele
 * da, damit sie nicht nachträglich die Plätze wechseln (ein Fingertipp träfe sonst ein anderes Ziel); ebenso wartet
 * „mehr“ auf den Jagdzustand, denn erst dann steht fest, ob ein Auslöser in `main` erscheint. Auch die Ansicht „mehr“
 * stellt ihre Ziele daraus zusammen.
 */
export function useStartlage(id: string) {
  const { uid } = useSitzung();
  const anzeige = useJagdzustand(id);
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${id}` : null);
  const rollen = (eintrag.daten?.["Rollen"] as string[] | undefined) ?? [];
  const zieleBekannt = (!!eintrag.daten || !eintrag.laedt) && anzeige.bekannt;
  const zustandsdokument = useDokument(`drueckjagden/${id}/jagdzustand/aktuell`);
  const beginne = ((zustandsdokument.daten?.["Jagdzeitplan"] as Array<{ Beginn?: Date }> | undefined) ?? [])
    .map((a) => a.Beginn).filter((b): b is Date => b instanceof Date);
  const phase = phaseZu(anzeige.jetzt, anzeige.zustand === "Jagd im Gange", beginne,
    anzeige.zustand === "Jagd ruht" && anzeige.eingriff?.art === "Hahn in Ruh");
  const revier = (eintrag.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  const beitraege = useStartbeitraege({ drueckjagdId: id, uid, rollen, revier, phase });
  return { uid, anzeige, rollen, revier, zieleBekannt, beitraege: zieleBekannt ? beitraege : [] };
}

/**
 * Bestätigung nach einer Meldung (design/README §6, §9.3, §9.4, `dauer.halten`): Sie steht bis zur nächsten Berührung,
 * mindestens 3 s; Notruf und „Rückgängig“ wirken immer sofort (A-f1, A-f2), jede andere Berührung vor Ablauf der 3 s wird
 * verworfen, danach beendet sie die Bestätigung und löst selbst nichts aus. Dann rückt die Leiste in die Zeile der
 * Sekundärziele und die Hauptaktion ist wieder bedienbar, bis das Rückgängig-Fenster endet.
 */
function haltenMs(): number {
  const wert = getComputedStyle(document.documentElement).getPropertyValue("--dj-bewegung-dauer-halten").trim();
  const m = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(wert);
  return m ? Number(m[1]) * (m[2] === "s" ? 1000 : 1) : 3000;
}

function useBestaetigung(meldungsId: string | null): boolean {
  const [beendet, setBeendet] = useState<string | null>(null);
  useEffect(() => {
    if (!meldungsId || beendet === meldungsId) return;
    let gehalten = false;
    const t = window.setTimeout(() => { gehalten = true; }, haltenMs());
    const beruehrt = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest(".rg, .k--notruf")) return;
      if (!gehalten) {
        // vor Ablauf der Haltezeit: die Berührung wird verworfen (Schutz gegen den Doppeltipp)
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      setBeendet(meldungsId);
    };
    window.addEventListener("pointerdown", beruehrt, true);
    return () => { window.clearTimeout(t); window.removeEventListener("pointerdown", beruehrt, true); };
  }, [meldungsId, beendet]);
  return !!meldungsId && beendet !== meldungsId;
}

/**
 * Eingabesperre der Startansicht (design/README §6, §10.1 Regel B): beim Öffnen, beim Erscheinen der Bestätigung nach
 * einer Meldung (ein zweiter Tipp eines Doppeltipps trifft sonst die Rückgängig-Leiste oder den Notruf) und, kritisch,
 * wenn nach dem Rückgängig-Fenster die Sekundärziele zurückkehren. Endet die Bestätigung durch eine Berührung, ist die
 * Hauptaktion sofort bedienbar (A-01): dieselbe Meldung, keine neue Sperre.
 */
function useSperreDerStartansicht(id: string, meldungsId: string | null): boolean {
  const nachMeldung = useRef(false);
  const art = meldungsId || !nachMeldung.current ? "ablauf" : "kritisch";
  useEffect(() => {
    if (meldungsId) nachMeldung.current = true;
  }, [meldungsId]);
  return useSperre(art, `${id}|${meldungsId ?? ""}`);
}

/**
 * Höhenbudget der Hinweise (Bericht N13, K92 3, K99, K105): Das Budget der Startansicht umfasst Zustandsband, Hinweise,
 * Sekundärziele (genau eine Reihe, N14), Hauptaktion, Auslöser und Notruf. `main` schrumpft nie unter seinen Inhalt (Hauptaktion, Auslöser, Notruf;
 * app.css `minmax(min-content, 1fr)`). Ist die Ansicht dadurch höher als der Bildschirm, nimmt die Startansicht Hinweise von
 * hinten heraus (Reihenfolge nach Wichtigkeit: Daten fehlen, Kalb vor Alttier, iPhone ohne Home-Bildschirm), bis alles ganz
 * sichtbar ist — nie verdrängt ein Hinweis den Notruf, nie steht er angeschnitten (design/README §1 Nr. 5). Gemessen wird der
 * Überlauf der Ansicht selbst, nicht die Verteilung des Rasters (Chrome und WebKit verteilen freien Platz auf `minmax(0, auto)`
 * verschieden). Ändert sich die Größe der Ansicht (Drehen, Browserleisten, Schriftgröße) oder ihr Inhalt (Laden der Ziele, ein
 * Hinweis erscheint), beginnt die Rechnung neu.
 */
const ANZAHL_HINWEISE = 3;

/** Plätze der Reihe der Sekundärziele vor „mehr“ (K105: eine Reihe, höchstens drei Spalten, die letzte „mehr“). */
const SEKUNDAERZIELE_VOR_MEHR = 2;

function useHinweiseImBudget(anzahl: number): { ref: (e: HTMLDivElement | null) => void; sichtbar: number } {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [sichtbar, setSichtbar] = useState(anzahl);
  const [groesse, setGroesse] = useState("");
  useEffect(() => {
    const ansicht = element?.parentElement;
    if (!ansicht) return;
    let aenderung = 0;
    const masse = new ResizeObserver(() => setGroesse(`${ansicht.clientWidth}x${ansicht.clientHeight}`));
    masse.observe(ansicht);
    // Inhalt ändert sich (Ziele und Hauptaktion nach dem Laden, ein Hinweis erscheint oder ändert seinen Text): neu rechnen
    const inhalt = new MutationObserver(() => setGroesse(`inhalt-${++aenderung}`));
    inhalt.observe(ansicht, { childList: true, subtree: true });
    inhalt.observe(element, { childList: true, subtree: true, characterData: true });
    return () => { masse.disconnect(); inhalt.disconnect(); };
  }, [element]);
  useLayoutEffect(() => { setSichtbar(anzahl); }, [groesse, anzahl]);
  useLayoutEffect(() => {
    const ansicht = element?.parentElement;
    if (!ansicht || sichtbar <= 0) return;
    if (ansicht.scrollHeight > ansicht.clientHeight + 1) setSichtbar((n) => n - 1);
  });
  return { ref: setElement, sichtbar };
}

/** Stand der Abhol- bzw. Bergeliste als Zahlen, großer Inhalt der Startansicht (design/README §4.4, K92 1, Review 0f O-f2). */
function Listenlage({ zahlen }: { zahlen: Array<[number, string]> }) {
  return (
    <p className="start__lage liste__zahlen platte" role="status">
      {zahlen.map(([z, w]) => <span key={w} className="zahl"><b>{z}</b> <span>{w}</span></span>)}
    </p>
  );
}

function useListenlage(id: string, revier: string | null, haupt: Kernaktion | undefined): Array<[number, string]> | null {
  const art = haupt?.begriff === "Abholliste" ? "abholliste" : haupt?.begriff === "Bergeliste" ? "bergeliste" : null;
  const liste = useSammlung(art && revier ? `drueckjagden/${id}/reviere/${revier}/${art}` : null).daten;
  if (!art) return null;
  if (art === "abholliste") {
    const status = (s: string) => liste.filter((e) => e["Anstellstatus"] === s).length;
    return [[liste.length, "Stände"], [status("am Stand"), "am Stand"], [status("abgeholt"), "abgeholt"]];
  }
  return [[liste.length, "Stücke"], [liste.filter((e) => e["Erlegungsstatus"] === "geborgen").length, "geborgen"]];
}

/** Zusammenfassung der Lage auf der Startansicht der Leitung (design/README §4.4 K70, `#s-leitung`). */
function useLeitungslage(id: string, leitung: boolean): string | null {
  const notrufe = useSammlung(leitung ? `drueckjagden/${id}/offene_notrufe` : null).daten;
  const vermisst = useSammlung(leitung ? `drueckjagden/${id}/vermisste_hunde` : null).daten;
  if (!leitung) return null;
  return `Notruf ${notrufe.length} · Hund vermisst ${vermisst.filter((v) => v["Vermisststatus"] !== "zurück").length}`;
}

export function Startansicht() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { uid, anzeige, rollen, revier, zieleBekannt, beitraege } = useStartlage(id);
  const aufgaben = useDokument(uid ? `drueckjagden/${id}/meine_aufgaben/${uid}` : null);
  const rg = useRueckgaengig();
  const bestaetigung = useBestaetigung(rg.offen?.umschlag.meldungsId ?? null);
  const gesperrt = useSperreDerStartansicht(id, rg.offen?.umschlag.meldungsId ?? null);
  const beschaeftigt = useEingabesperre(gesperrt) || undefined;
  const offen = useWartend();
  const kalb = useKalbVorAlttier(id);
  const wurzel = useGestaltung();
  // vor den frühen Rückgaben (Bestätigungsdialoge): Hooks immer in derselben Reihenfolge
  const budget = useHinweiseImBudget(ANZAHL_HINWEISE);
  const stand = aufgaben.daten?.["Stand"] as string | undefined;
  const person = aufgaben.daten?.["_person"] as Personenangabe | undefined;
  const ausloeser = useAusloeser(id, anzeige);
  // „Automatisch“ (design/README §2.4): nur hier umschalten, nie während einer Meldung; Ort = Stand (letzte Messung),
  // sonst die Lage der Drückjagd
  const rettungspunkte = useSammlung(`drueckjagden/${id}/rettungspunkte`).daten;
  useAutomatik(letzteMessung()?.position ?? ortAusRettungspunkten(rettungspunkte), !rg.offen);

  // Kernaktionen der Kontexte (plattform/startansicht.ts): die Hauptaktion der zuerst genannten Rolle in `main`, die
  // übrigen Kernaktionen direkt in `navigation` „Drückjagd“ (§3.1 V-12, §4.3).
  const ziele = zieleDerStartansicht(beitraege, zieleBekannt ? rollen : [], id);
  const bereiche = beitraege.map((b) => b.bereiche).filter(Boolean) as ReactNode[];
  // Suchbereich (§4.4) an Stelle der Hauptaktion; die Hauptaktion bleibt dann direkt in `navigation` „Drückjagd“
  const suche = bereiche.length > 0;
  const haupt = ziele.haupt;
  const leitung = !suche && haupt?.begriff === "Lagebild" && rollen.some((r) => r === "Jagdherr" || r === "Jagdleiter");
  // Sekundärziele in genau einer Reihe (Anforderungen K105, design/README §14 N14): höchstens drei Spalten, die letzte ist
  // immer „mehr“. Kernaktionen weiterer Rollen zuerst (§3.1 V-12, V-29) mit ihrem Platz (`data-platz`), meldende vorn und
  // immer direkt (notfalls vierte kompakte Spalte, `data-meldend`); danach weitere Ziele bis zwei Plätze belegt sind; ihre
  // Priorität zählt hinter den Kernaktionen weiter, so dass bei niedriger Ansicht oder großer Schrift (Priorität 2 und 3
  // entfallen) nur Kernaktionen, Priorität 1 und „mehr“ bleiben. Eine Kernaktion ohne meldende Handlung, die in der Reihe
  // keinen Platz hat (app.css: Platz ≥ 3, neben einer 80-px-Kachel der Leitung oder der Liste Platz ≥ 2), steht in „mehr“.
  const reihe = kernaktionenDerReihe(ziele, suche);
  const kernAnzahl = reihe.length;
  const weitere = offen ? [...ziele.weitere.filter((z) => z.begriff === "Meldungen"), ...ziele.weitere.filter((z) => z.begriff !== "Meldungen")] : ziele.weitere;
  const direkt = weitere.slice(0, Math.max(0, SEKUNDAERZIELE_VOR_MEHR - kernAnzahl));
  // Ansteller, Bergetrupp (design/README §4.4, §14 0g K92 1): keine große Hauptaktion; „Abholliste“/„Bergeliste“ ist eine
  // 80-px-Kachel links in der Zeile der weiteren Ziele, der Stand der Liste (Zahlen) der große Inhalt
  const listenziel = !suche && (haupt?.begriff === "Abholliste" || haupt?.begriff === "Bergeliste");
  const lage = useLeitungslage(id, leitung);
  const zahlen = useListenlage(id, revier, listenziel ? haupt : undefined);
  const antippen = (z: { ziel: string; ausloesen?: () => void }) => (z.ausloesen ? z.ausloesen() : navigate(z.ziel));
  const notruf = <Kachel symbol="notruf" begriff="Notruf" art="notruf" onPress={() => navigate(`/drueckjagd/${id}/notruf`)} />;

  if (ausloeser.dialog) return ausloeser.dialog({ name: "Startansicht" });
  // Bestätigungsdialog eines Kontexts (Nachsuchenergebnis, §5) an Stelle der Startansicht
  const startdialog = beitraege.find((b) => b.dialog)?.dialog;
  if (startdialog) {
    return (
      <DialogRahmen drueckjagdId={id} anzeige={anzeige} name={startdialog.name} titel={startdialog.titel} schluessel={startdialog.name}>
        {startdialog.inhalt}
      </DialogRahmen>
    );
  }

  const kontext = (
    <Kontextzeile symbol={ROLLENSYMBOL[rollen[0] as Rolle] ?? "teilnehmer"}
      teile={[stand ? `Stand ${stand}` : null, person?.Name].filter((t): t is string => !!t)}>
      {offen ? <span className="ctx__status" role="status"><Symbol name="warteschlange" /><span>{offen} noch nicht übertragen</span></span> : null}
      <OfflineBereit drueckjagdId={id} className="ctx__status" />
    </Kontextzeile>
  );
  const hinweise = [<OfflineHinweis key="offline" />, kalb && !(rg.offen && !rg.offen.abgelaufen) ? <Status key="kalb">Kalb vor Alttier</Status> : null, <IphoneHinweis key="iphone" />];

  const sekundaer = (
    <nav className="sek" aria-label="Drückjagd" data-gleich="" inert={gesperrt || bestaetigung || undefined}>
      {zieleBekannt ? (
        <>
          {reihe.map((z) => (
            <Kachel key={z.begriff} symbol={z.symbol} begriff={z.begriff} art="sek" platz={z.platz} meldend={z.meldend} fuge={mitFuge(z.begriff)} className={zweizeilig(z.begriff)}
              onPress={() => antippen(z)} />
          ))}
          {direkt.map((z, i) => (
            <Kachel key={z.begriff} symbol={z.symbol} begriff={z.begriff} art="sek" prio={kernAnzahl + i + 1} fuge={mitFuge(z.begriff)} className={zweizeilig(z.begriff)}
              marke={z.begriff === "Meldungen" ? offen || null : null} onPress={() => antippen(z)} />
          ))}
          <Kachel symbol="mehr" begriff="mehr" art="sek" className="sek__mehr" onPress={() => navigate(`/drueckjagd/${id}/mehr`)} />
        </>
      ) : null}
    </nav>
  );

  const feld = ["feld", "feld--start", leitung ? "feld--leitung-start" : null, suche ? "feld--suche" : null].filter(Boolean).join(" ");
  return (
    <div ref={wurzel} className={`ansicht${gesperrt ? " ist-gesperrt" : ""}`} data-zustand={anzeige.flaeche}>
      <Zustandsband drueckjagdId={id} anzeige={anzeige} art="start" kontext={kontext} />
      <div ref={budget.ref} className="start__hinweise">
        {hinweise.map((h, i) => (h ? <div key={h.key} className="start__hinweis" hidden={i >= budget.sichtbar || undefined}>{h}</div> : null))}
      </div>
      {rg.offen && !bestaetigung ? <RueckgaengigLeiste offen={rg.offen} onRueckgaengig={rg.zuruecknehmen} /> : sekundaer}
      <main className={feld} aria-label="Startansicht" inert={gesperrt} aria-busy={beschaeftigt}>
        {rg.offen && bestaetigung ? (
          // Nach einer Meldung (§9.3, §9.4; oberflaeche.md §5): Bestätigung über dem Hauptbereich, darunter die
          // Rückgängig-Leiste direkt über dem Notruf (Daumenreichweite); die Sekundärziele entfallen in dieser Zeit.
          <>
            <Bestaetigung offen={rg.offen} kontext={stand ? `Stand ${stand}` : undefined} />
            <RueckgaengigLeiste offen={rg.offen} onRueckgaengig={rg.zuruecknehmen} />
            {notruf}
          </>
        ) : (
          <>
            {haupt && !suche ? (
              <Kachel symbol={haupt.symbol} begriff={haupt.begriff} art={listenziel ? "sek" : "haupt"}
                className={leitung ? "k--haupt-leitung" : listenziel ? "k--listenziel" : undefined}
                onPress={() => antippen({ ziel: haupt.pfad, ...(haupt.ausloesen ? { ausloesen: haupt.ausloesen } : {}) })} />
            ) : null}
            {suche ? bereiche : null}
            {leitung && lage ? <div className="leitung__lage platte" role="status" data-zeile-fest="">{lage}</div> : null}
            {zahlen ? <Listenlage zahlen={zahlen} /> : null}
            {ausloeser.kachel}
            {notruf}
          </>
        )}
      </main>
    </div>
  );
}

/** Sekundärziel mit Wortfuge (design/README K96: „Kontroll-/liste“ der Leitung; als Hauptaktion bricht es nicht). */
function mitFuge(begriff: string): boolean {
  return begriff === "Kontrollliste";
}

/** Sekundärziel mit zweizeiligem Begriff („Hunde im Treiben“): Symbol kleiner, zwei Zeilen à 15 px (design/README §9.1). */
function zweizeilig(begriff: string): string | undefined {
  return (begriff.includes(" ") && begriff.length > 12) || mitFuge(begriff) ? "k--zweizeilig" : undefined;
}
